import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { adminLogin } from '../pages/Login/login.service';
import { LoginRequest } from '../pages/Login/login.dto';
import { isTokenExpired } from '../utils/jwt';

interface Admin {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  admin: Admin | null;
  token: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('admin_token');
      const adminData = localStorage.getItem('admin_data');

      if (token && adminData) {
        // An expired token previously wasn't checked here at all - it was
        // treated as a valid session until the next API call happened to
        // 401 and trigger a logout. Check it up front instead: an admin
        // with a stale tab open doesn't get a false "still logged in"
        // state, and doesn't send an already-expired token on their next
        // action only to be bounced.
        if (isTokenExpired(token)) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_data');
        } else {
          try {
            const parsedAdmin = JSON.parse(adminData);
            setAdmin(parsedAdmin);
            setToken(token);
            setIsAuthenticated(true);
          } catch {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_data');
          }
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminLogin(credentials);
      localStorage.setItem('admin_token', response.access_token);
      localStorage.setItem('admin_data', JSON.stringify(response.admin));

      setAdmin(response.admin);
      setToken(response.access_token);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('AuthContext: Login failed', error);
      setError(error instanceof Error ? error.message : 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    setAdmin(null);
    setToken(null);
    setIsAuthenticated(false);
    setError(null);
  };

  const value = {
    isAuthenticated,
    admin,
    token,
    login,
    logout,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
